import type { KeyboardEvent } from 'react';

import { usePortalLocale } from '@/lib/i18n/PortalLocaleContext';

export function TutorSectionTabs<T extends string>({
  label,
  items,
  value,
  onChange,
  panelId,
}: {
  label: string;
  items: { id: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
  panelId: string;
}) {
  const { t } = usePortalLocale();
  function move(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    const offset = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (!offset && event.key !== 'Home' && event.key !== 'End') return;
    event.preventDefault();
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? items.length - 1
          : (index + offset + items.length) % items.length;
    onChange(items[next].id);
    const tabs =
      event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
    tabs?.[next]?.focus();
  }
  return (
    <div
      role="tablist"
      aria-label={t(label)}
      className="flex flex-wrap gap-2 rounded-2xl border border-hairline bg-canvas-pure p-2"
    >
      {items.map((item, index) => (
        <button
          key={item.id}
          id={`${panelId}-tab-${item.id}`}
          role="tab"
          type="button"
          aria-selected={value === item.id}
          aria-controls={panelId}
          tabIndex={value === item.id ? 0 : -1}
          onKeyDown={(event) => move(event, index)}
          onClick={() => onChange(item.id)}
          className={`min-h-11 rounded-xl px-4 py-2 text-sm font-bold ${value === item.id ? 'bg-ink text-canvas' : 'text-ink-soft hover:bg-surface'}`}
        >
          {t(item.label)}
        </button>
      ))}
    </div>
  );
}
